import { useContext, useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSearchIcon,
  deDE,
} from "@mui/x-data-grid";

import {
  Alert,
  AlertColor,
  Box,
  Grid,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";

import { UserProgramContext } from "../../context/user-program/user-program";
import { Navbar } from "../styled/nav-bar";
import { useDebounce } from "../../hooks/useDebounce";
import {
  getUsersClinicsStudoq,
  sendEmailFirstPresentation,
  updateOperationDate,
} from "../../api/users-clinic-dashboard";
import { ContainedButton } from "../styled/contained-button";
import { IUserDataStudoq } from "../../interface/IUserDataStudoq";
import { DateModal } from "./date-modal";

export const StudoqPage = () => {
  const { userPayload, program } = useContext(UserProgramContext);
  const [users, setUsers] = useState<IUserDataStudoq[]>([]);
  const [searchParams] = useSearchParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedDate, setSelectedDate] = useState<
    Record<string, dayjs.Dayjs | null>
  >(
    users.reduce(
      (acc, user) => {
        acc[user._id] = null;
        return acc;
      },
      {} as Record<string, dayjs.Dayjs | null>
    )
  );

  const [searchValue, setSearchValue] = useState(
    searchParams.get("filter") ?? ""
  );
  const [pageState, setPageState] = useState({
    loading: true,
    currentPage: searchParams.get("page")
      ? Number(searchParams.get("page"))
      : 1,
    perPage: 10,
    totalUsers: 0,
    totalPages: 0,
  });

  const { _id: userId } = userPayload as { _id: string };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const debounceValue = useDebounce(searchValue, 500);
  const clinicParam =
    program?.clinicCode && program.clinicCode.length > 0
      ? program.clinicCode
      : "PWS-001";

  const fetchData = useCallback(async () => {
    setPageState((prevPageState) => ({ ...prevPageState, loading: true }));
    try {
      const data = await getUsersClinicsStudoq({
        perPage: pageState.perPage,
        currentPage: pageState.currentPage,
        searchValue: debounceValue,
        clinicCode: clinicParam as string,
      });

      setUsers(data.users);

      setPageState((prevPageState) => ({
        ...prevPageState,
        totalUsers: data.totalUsers,
        totalPages: data.totalPages,
        loading: false,
      }));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [debounceValue, pageState.currentPage, pageState.perPage, clinicParam]);

  const sendEmailEvent = async (
    patientUserId: string,
    followupType: string
  ) => {
    try {
      setPageState((prevPageState) => ({ ...prevPageState, loading: true }));
      await sendEmailFirstPresentation(patientUserId, followupType, userId);
      setPageState((prevPageState) => ({ ...prevPageState, loading: false }));

      await fetchData();

      setSnackbarMessage("E-Mail erfolgreich gesendet.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Error sending email or creating Studoq:", error);
      setSnackbarMessage("Beim Senden der E-Mail ist ein Fehler aufgetreten.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setPageState((prevPageState) => ({ ...prevPageState, loading: false }));
    }
  };

  const handleDateChange = (id: string, newDate: dayjs.Dayjs) => {
    setSelectedDate((prevDates) => ({
      ...prevDates,
      [id]: newDate,
    }));
  };

  const updateSelectedDate = async (newDate: dayjs.Dayjs, id: string) => {
    try {
      const response = await updateOperationDate(newDate, id);
      if (response) {
        handleDateChange(id, newDate);
        setSnackbarMessage("OP-Datum erfolgreich aktualisiert.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating operation date:", error);
      setSnackbarMessage(
        "Beim Aktualisieren des OP-Datums ist ein Fehler aufgetreten."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const dateAfterOperation = (date: string, months: number) => {
    const inputDate = dayjs(date, "DD/MM/YYYY", true);
    if (!inputDate.isValid()) {
      console.error("Fecha inválida:", date);
      return false;
    }

    const dateToComparete = inputDate.add(months, "month");
    const today = dayjs();

    return dateToComparete.isBefore(today);
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", flex: 0.5, hideable: true, hide: true },
    { field: "firstName", headerName: "Vorname", flex: 0.5 },
    { field: "lastName", headerName: "Nachname", flex: 0.5 },
    { field: "email", headerName: "E-Mail", flex: 0.5 },
    {
      field: "firstPresentationStatus",
      headerName: "Erstvorstellung",
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params: GridRenderCellParams) => {
        const user = users.find((user) => user._id === params.id);

        if (!user) {
          return null;
        }

        const firstPresentationFollowup = user.studoq?.followups?.find(
          (followup) => followup.followupType === "firstPresentation"
        );

        if (!firstPresentationFollowup) {
          return (
            <ContainedButton
              key={user._id}
              onClick={() => sendEmailEvent(user._id, "firstPresentation")}
            >
              <Typography sx={{ fontSize: "0.8rem" }}>
                Fragebogen senden
              </Typography>
            </ContainedButton>
          );
        }

        if (
          firstPresentationFollowup.sendEmail ||
          firstPresentationFollowup.completed
        ) {
          return (
            <CheckBoxRoundedIcon
              key={firstPresentationFollowup._id}
              fontSize="large"
              color="success"
            />
          );
        }

        return (
          <ContainedButton
            key={user._id}
            onClick={() => sendEmailEvent(user._id, "firstPresentation")}
          >
            <Typography sx={{ fontSize: "0.8rem" }}>
              Fragebogen senden
            </Typography>
          </ContainedButton>
        );
      },
    },
    {
      field: "datumEintragen",
      headerName: "OP-Datum eintragen",
      flex: 0.8,
      align: "right",
      headerAlign: "center",
      renderCell: (params) => {
        const operationDate = users.find((user) => user._id === params.id)
          ?.studoq?.operationDate;

        return (
          <Box
            sx={{
              height: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DateModal
              userId={params.id as string}
              operationDate={operationDate ? dayjs(operationDate) : null}
              updateSelectedDate={updateSelectedDate}
              onDateChange={(userId, date) => {
                setSelectedDate((prev) => ({
                  ...prev,
                  [userId]: date ?? null,
                }));
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "stuDoQ-nach-op",
      headerName: "StuDoQ nach OP",
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const user = users.find((user) => user._id === params.id);
        if (!user) {
          return null;
        }

        const firstPresentationFollowup = user.studoq?.followups?.find(
          (followup) => followup.followupType === "firstPresentation"
        );

        const threeMonthFollowup = user.studoq?.followups?.find(
          (followup) => followup.followupType === "3Months"
        );

        if (threeMonthFollowup) {
          if (threeMonthFollowup.sendEmail || threeMonthFollowup.completed) {
            return (
              <CheckBoxRoundedIcon
                key={threeMonthFollowup._id}
                fontSize="large"
                color="success"
              />
            );
          }

          return (
            <ContainedButton
              key={threeMonthFollowup._id || user._id}
              onClick={() => sendEmailEvent(user._id, "3Months")}
              disabled={
                !firstPresentationFollowup?.completed ||
                !user.studoq?.operationDate ||
                !dateAfterOperation(
                  dayjs(user.studoq?.operationDate).format("DD/MM/YYYY"),
                  3
                )
              }
            >
              <Typography sx={{ fontSize: "0.8rem" }}>Starten</Typography>
            </ContainedButton>
          );
        }

        return (
          <ContainedButton
            key={user._id}
            onClick={() => sendEmailEvent(user._id, "3Months")}
            disabled={
              !firstPresentationFollowup?.completed ||
              !user.studoq?.operationDate ||
              !dateAfterOperation(
                dayjs(user.studoq?.operationDate).format("DD/MM/YYYY"),
                3
              )
            }
          >
            <Typography sx={{ fontSize: "0.8rem" }}>Starten</Typography>
          </ContainedButton>
        );
      },
    },
    {
      field: "annualFollowUp",
      hide: true,
      hideable: true,
      headerName: "1. Jährliche Nachsorge",
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const user = users.find((user) => user._id === params.id);

        if (!user) {
          return null;
        }

        const threeMonthFollowup = user.studoq?.followups?.find(
          (followup) => followup.followupType === "3Months"
        );

        const annualFollowup = user.studoq?.followups?.find(
          (followup) => followup.followupType === "1Year"
        );

        if (annualFollowup) {
          if (annualFollowup.sendEmail || annualFollowup.completed) {
            return (
              <CheckBoxRoundedIcon
                key={annualFollowup._id}
                fontSize="large"
                color="success"
              />
            );
          }

          return (
            <ContainedButton
              key={annualFollowup._id || user._id}
              onClick={() => sendEmailEvent(user._id, "1Year")}
              disabled={
                !threeMonthFollowup?.completed ||
                !user.studoq?.operationDate ||
                !dateAfterOperation(
                  dayjs(user.studoq?.operationDate).format("DD/MM/YYYY"),
                  12
                )
              }
            >
              <Typography sx={{ fontSize: "0.8rem" }}>Starten</Typography>
            </ContainedButton>
          );
        }

        return (
          <ContainedButton
            key={user._id}
            onClick={() => sendEmailEvent(user._id, "1Year")}
            disabled={
              !threeMonthFollowup?.completed ||
              !user.studoq?.operationDate ||
              !dateAfterOperation(
                dayjs(user.studoq?.operationDate).format("DD/MM/YYYY"),
                12
              )
            }
          >
            <Typography sx={{ fontSize: "0.8rem" }}>Starten</Typography>
          </ContainedButton>
        );
      },
    },
  ];

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const getColumns = () => {
    if (isMobile) {
      return columns.map((column) => ({ ...column, flex: 0, width: 150 }));
    } else {
      return columns;
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData, selectedDate]);

  const mapPatientsToRows = () =>
    users.map((user) => ({
      id: user._id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      datumEintragen: {
        value: selectedDate[user._id],
        handleDateChange: (newDate: dayjs.Dayjs) =>
          handleDateChange(user._id, newDate),
      },
    }));

  const rows = mapPatientsToRows();

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      spacing={4}
      pt={2}
      pr={2}
      pl={2}
      pb={5}
    >
      <Navbar />

      <Box
        sx={{
          marginTop: "2em",
          marginRight: "2em",
          marginLeft: "2em",
          width: "80%",
        }}
      >
        <Grid container spacing={4} p={3}>
          <Grid item xs={12} sm={7}>
            <Typography variant="h5" fontWeight="bold">
              Patientenliste-StudoQ
            </Typography>
          </Grid>

          <Grid item xs={12} sm={5} justifyContent="end">
            <TextField
              fullWidth
              value={searchValue}
              label="Suche"
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon />
                  </InputAdornment>
                ),
              }}
              onInput={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", cursor: "pointer" }}>
            <DataGrid
              autoHeight
              rows={rows}
              rowHeight={70}
              columns={getColumns()}
              rowCount={pageState.totalUsers}
              loading={pageState.loading}
              rowsPerPageOptions={[5, 10, 15]}
              pagination
              page={pageState.currentPage - 1}
              pageSize={pageState.perPage}
              paginationMode="server"
              onPageChange={(newPage) =>
                setPageState((prev) => ({ ...prev, currentPage: newPage + 1 }))
              }
              onPageSizeChange={(newPageSize) =>
                setPageState({ ...pageState, perPage: newPageSize })
              }
              checkboxSelection={false}
              columnVisibilityModel={{
                id: false,
              }}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              sx={{
                ".MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                ".MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
              }}
            />
          </Grid>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
          >
            <Alert
              variant="filled"
              severity={snackbarSeverity as AlertColor | undefined}
              onClose={handleSnackbarClose}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Grid>
      </Box>
    </Grid>
  );
};
